import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithPhoneNumber,
  GoogleAuthProvider,
  updateProfile,
  RecaptchaVerifier,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { Eye, EyeOff, Phone, Mail, ArrowLeft } from 'lucide-react';
import { auth } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

const googleProvider = new GoogleAuthProvider();

function getPasswordStrength(pwd) {
  if (pwd.length === 0) return { level: 0, label: '' };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  if (pwd.length >= 12) score++;
  return { level: score, label: score <= 1 ? 'weak' : score <= 2 ? 'medium' : 'strong' };
}

export default function AuthPage() {
  const navigate   = useNavigate();
  const { syncProfile } = useAuth();
  const { lang }   = useLang();
  const tr         = t[lang];

  const [screen,   setScreen]   = useState('main'); // 'main' | 'forgot'
  const [authMode, setAuthMode] = useState('email'); // 'email' | 'phone'
  const [tab,      setTab]      = useState('signup');
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [termsOk,  setTermsOk]  = useState(false);
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [info,     setInfo]     = useState('');

  // Phone OTP
  const [phone,     setPhone]    = useState('');
  const [otp,       setOtp]      = useState('');
  const [otpSent,   setOtpSent]  = useState(false);
  const [confirm,   setConfirm]  = useState(null);
  const recaptchaRef = useRef(null);
  const rcpVerifier  = useRef(null);

  const strength = getPasswordStrength(password);
  const strengthColors = { weak: '#C0392B', medium: '#E67E22', strong: '#2D6A4F' };
  const strengthLabels = { weak: tr.passwordWeak || 'Weak', medium: tr.passwordMedium || 'Medium', strong: tr.passwordStrong || 'Strong' };

  // Handle Google redirect result on mount
  useEffect(() => {
    getRedirectResult(auth).then(async (result) => {
      if (result?.user) {
        await afterAuth(result.user);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Init RecaptchaVerifier
  useEffect(() => {
    if (authMode !== 'phone') return;
    if (!recaptchaRef.current || rcpVerifier.current) return;
    try {
      rcpVerifier.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible', callback: () => {} });
    } catch { /* silent */ }
    return () => {
      if (rcpVerifier.current) { try { rcpVerifier.current.clear(); } catch { /* */ } rcpVerifier.current = null; }
    };
  }, [authMode]);

  const afterAuth = async (fbUser, displayName) => {
    try {
      const profile = await syncProfile(fbUser, displayName || fbUser.displayName);
      navigate(profile?.chart ? '/home' : '/birth-details');
    } catch {
      setError('Account setup failed. Please try again.');
      setLoading(false);
    }
  };

  const friendlyError = (code) => {
    const map = {
      'auth/user-not-found':        'No account found. Please sign up first.',
      'auth/wrong-password':        'Incorrect password.',
      'auth/email-already-in-use':  'This email is already registered. Please sign in.',
      'auth/weak-password':         'Password must be at least 6 characters.',
      'auth/invalid-credential':    'Invalid email or password.',
      'auth/invalid-email':         'Please enter a valid email address.',
      'auth/popup-blocked':         'Popup was blocked. Please allow popups or try again.',
      'auth/popup-closed-by-user':  '',
      'auth/account-exists-with-different-credential': 'An account already exists with this email. Try signing in with email/password.',
      'auth/unauthorized-domain':   'This domain is not authorised for Google sign-in. Please contact support.',
      'auth/network-request-failed':'Network error. Please check your connection.',
      'auth/too-many-requests':     'Too many attempts. Please wait a few minutes.',
      'auth/invalid-phone-number':  'Invalid phone number. Please include country code (e.g. +91).',
      'auth/invalid-verification-code': 'Invalid OTP. Please check and try again.',
    };
    return map[code] || 'Something went wrong. Please try again.';
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!email.trim()) { setError('Email is required.'); return; }
    if (tab === 'signup') {
      if (!name.trim()) { setError('Full name is required.'); return; }
      if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
      if (!termsOk) { setError('Please accept the Terms & Privacy Policy to continue.'); return; }
    } else {
      if (!password) { setError('Password is required.'); return; }
    }
    setLoading(true);
    try {
      if (tab === 'signup') {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        if (name) await updateProfile(cred.user, { displayName: name });
        await afterAuth(cred.user, name);
      } else {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        await afterAuth(cred.user);
      }
    } catch (err) {
      const msg = friendlyError(err.code);
      if (msg) setError(msg);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(''); setInfo(''); setLoading(true);
    try {
      // Try popup first, fall back to redirect on mobile
      try {
        const result = await signInWithPopup(auth, googleProvider);
        await afterAuth(result.user);
      } catch (popupErr) {
        if (popupErr.code === 'auth/popup-blocked' || popupErr.code === 'auth/popup-closed-by-user') {
          // Try redirect
          await signInWithRedirect(auth, googleProvider);
        } else {
          throw popupErr;
        }
      }
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(friendlyError(err.code));
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo('Password reset email sent! Check your inbox (and spam folder).');
    } catch (err) {
      setError(friendlyError(err.code));
    }
    setLoading(false);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim()) return;
    setLoading(true);
    try {
      if (!rcpVerifier.current) {
        rcpVerifier.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible', callback: () => {} });
      }
      const confirmation = await signInWithPhoneNumber(auth, phone, rcpVerifier.current);
      setConfirm(confirmation);
      setOtpSent(true);
    } catch (err) {
      setError(friendlyError(err.code) || err.message);
      if (rcpVerifier.current) { try { rcpVerifier.current.clear(); } catch { /* */ } rcpVerifier.current = null; }
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!confirm || !otp) return;
    setLoading(true);
    try {
      const result = await confirm.confirm(otp);
      await afterAuth(result.user);
    } catch (err) {
      setError(friendlyError(err.code));
    }
    setLoading(false);
  };

  const switchMode = (mode) => {
    setAuthMode(mode); setError(''); setOtpSent(false); setOtp(''); setPhone(''); setConfirm(null);
  };

  // ── Forgot Password Screen ──
  if (screen === 'forgot') {
    return (
      <div data-testid="auth-page" style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: '40px 24px', background: '#0A0F1E',
      }}>
        <button onClick={() => { setScreen('main'); setError(''); setInfo(''); }}
          style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, marginBottom: 32, padding: 0, alignSelf: 'flex-start' }}>
          <ArrowLeft size={18} /> Back
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700, marginBottom: 8 }}>
          Reset Password
        </h1>
        <p style={{ fontSize: 14, color: '#8A8580', marginBottom: 28, lineHeight: 1.5 }}>
          Enter your email and we'll send you a link to reset your password.
        </p>
        <form onSubmit={handleForgotPassword} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input data-testid="input-email" type="email" className="input-field" placeholder={tr.email || 'Email address'} value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
          {error && <p data-testid="auth-error" style={{ color: '#C0392B', fontSize: 13 }}>{error}</p>}
          {info  && <p data-testid="auth-info"  style={{ color: '#2D6A4F', fontSize: 13 }}>{info}</p>}
          <button data-testid="reset-pwd-btn" type="submit" className="btn-primary" disabled={loading}>
            {loading ? (tr.loading || 'Please wait…') : 'Send Reset Link'}
          </button>
        </form>
      </div>
    );
  }

  // ── Main Auth Screen ──
  return (
    <div data-testid="auth-page" style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      justifyContent: 'center', padding: '40px 24px 56px',
      background: '#0A0F1E', boxSizing: 'border-box', width: '100%',
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 28, color: '#C9A84C', marginBottom: 8 }}>✦</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#F5F0E8', fontWeight: 700 }}>
          {tr.appName || 'DailyRaashi'}
        </h1>
        <p style={{ fontSize: 13, color: '#8A8580', marginTop: 4 }}>{tr.tagline || 'Your stars. Your day.'}</p>
      </div>

      {/* Email / Phone mode switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'email', icon: Mail,  label: tr.emailTab || 'Email' },
          { key: 'phone', icon: Phone, label: tr.phoneTab || 'Phone' },
        ].map(({ key, icon: Icon, label }) => (
          <button key={key} data-testid={`auth-mode-${key}`} onClick={() => switchMode(key)}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10,
              border: `1px solid ${authMode === key ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`,
              background: authMode === key ? 'rgba(201,168,76,0.1)' : 'transparent',
              color: authMode === key ? '#C9A84C' : '#8A8580',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'all 0.2s',
            }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ── EMAIL ── */}
      {authMode === 'email' && (
        <>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 4, marginBottom: 20 }}>
            {['signup','login'].map(k => (
              <button key={k} data-testid={`tab-${k}`}
                onClick={() => { setTab(k); setError(''); setInfo(''); }}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600, transition: 'all 0.2s',
                  background: tab === k ? '#C9A84C' : 'transparent',
                  color: tab === k ? '#0A0F1E' : '#8A8580',
                }}>
                {k === 'signup' ? (tr.signUp || 'Sign Up') : (tr.logIn || 'Sign In')}
              </button>
            ))}
          </div>

          <form onSubmit={handleEmailAuth} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {tab === 'signup' && (
              <input data-testid="input-name" type="text" className="input-field"
                placeholder={tr.name || 'Full name'} value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
            )}
            <input data-testid="input-email" type="email" className="input-field"
              placeholder={tr.email || 'Email address'} value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />

            <div style={{ position: 'relative' }}>
              <input data-testid="input-password" type={showPwd ? 'text' : 'password'} className="input-field"
                placeholder={tr.password || 'Password'} value={password} onChange={e => setPassword(e.target.value)}
                autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
                style={{ paddingRight: 48 }} />
              <button type="button" data-testid="toggle-password-btn" onClick={() => setShowPwd(v => !v)}
                style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#8A8580', cursor: 'pointer', padding: 0, display: 'flex' }}>
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {tab === 'signup' && password.length > 0 && (
              <div data-testid="password-strength">
                <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                  {[1,2,3].map(i => (
                    <div key={i} style={{ flex: 1, height: 3, borderRadius: 2,
                      background: strength.level >= i ? strengthColors[strength.label] : 'rgba(255,255,255,0.08)', transition: 'background 0.2s' }} />
                  ))}
                </div>
                <p style={{ fontSize: 11, color: strengthColors[strength.label] || '#8A8580' }}>
                  {strengthLabels[strength.label] || ''}
                </p>
              </div>
            )}

            {tab === 'signup' && (
              <label data-testid="terms-checkbox-label" style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${error?.includes('Terms') ? 'rgba(192,57,43,0.5)' : 'rgba(255,255,255,0.06)'}`,
                background: error?.includes('Terms') ? 'rgba(192,57,43,0.06)' : 'transparent',
                transition: 'all 0.2s',
              }}>
                <input data-testid="terms-checkbox" type="checkbox" checked={termsOk} onChange={e => setTermsOk(e.target.checked)}
                  style={{ marginTop: 3, accentColor: '#C9A84C', width: 16, height: 16, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#8A8580', lineHeight: 1.5 }}>
                  {tr.termsAgree || 'I agree to the'}{' '}
                  <button type="button" onClick={() => navigate('/terms')}
                    style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                    Terms &amp; Privacy Policy
                  </button>
                </span>
              </label>
            )}

            {error && <p data-testid="auth-error" style={{ color: '#C0392B', fontSize: 13 }}>{error}</p>}
            {info  && <p data-testid="auth-info"  style={{ color: '#2D6A4F', fontSize: 13 }}>{info}</p>}

            <button data-testid="email-auth-btn" type="submit" className="btn-primary" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? (tr.loading || 'Please wait…') : (tab === 'signup' ? (tr.signUp || 'Sign Up') : (tr.logIn || 'Sign In'))}
            </button>

            {tab === 'login' && (
              <button type="button" data-testid="forgot-password-btn" onClick={() => { setScreen('forgot'); setError(''); setInfo(''); }}
                style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', fontSize: 13, textAlign: 'center', padding: 4 }}>
                Forgot Password?
              </button>
            )}
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
            <span style={{ color: '#8A8580', fontSize: 13 }}>{tr.orLabel || 'or'}</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>

          <button data-testid="google-auth-btn" className="btn-secondary" onClick={handleGoogle} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.706c-.18-.54-.282-1.117-.282-1.706s.102-1.166.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {tr.continueGoogle || 'Continue with Google'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#8A8580' }}>
            {tab === 'signup' ? (tr.alreadyAccount || 'Already have an account?') : (tr.newHere || "Don't have an account?")}
            <button data-testid="switch-tab-btn"
              onClick={() => { setTab(tab === 'signup' ? 'login' : 'signup'); setError(''); setInfo(''); }}
              style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, marginLeft: 4 }}>
              {tab === 'signup' ? (tr.logIn || 'Sign In') : (tr.signUp || 'Sign Up')}
            </button>
          </p>
        </>
      )}

      {/* ── PHONE OTP ── */}
      {authMode === 'phone' && (
        <>
          {!otpSent ? (
            <form onSubmit={handleSendOtp} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input data-testid="input-phone" type="tel" className="input-field"
                placeholder={tr.phoneNumber || 'Phone number (+91...)'} value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" />
              {error && <p data-testid="auth-error" style={{ color: '#C0392B', fontSize: 13 }}>{error}</p>}
              <button data-testid="send-otp-btn" type="submit" className="btn-primary" disabled={loading}>
                {loading ? (tr.loading || 'Please wait…') : (tr.sendOtp || 'Send OTP')}
              </button>
              <div id="recaptcha-container" ref={recaptchaRef} />
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 13, color: '#8A8580', textAlign: 'center' }}>
                OTP sent to <strong style={{ color: '#F5F0E8' }}>{phone}</strong>
              </p>
              <input data-testid="input-otp" type="number" className="input-field"
                placeholder={tr.enterOtp || 'Enter 6-digit OTP'} value={otp} onChange={e => setOtp(e.target.value)}
                maxLength={6} style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.2em' }} />
              {error && <p data-testid="auth-error" style={{ color: '#C0392B', fontSize: 13 }}>{error}</p>}
              <button data-testid="verify-otp-btn" type="submit" className="btn-primary" disabled={loading}>
                {loading ? (tr.loading || 'Please wait…') : (tr.verifyOtp || 'Verify OTP')}
              </button>
              <button type="button" data-testid="change-phone-btn" className="btn-secondary"
                onClick={() => { setOtpSent(false); setOtp(''); setConfirm(null); setError(''); }}>
                Change Number
              </button>
            </form>
          )}
        </>
      )}

      {/* Legal links */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 28 }}>
        <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: '#8A8580', cursor: 'pointer', fontSize: 11 }}>Privacy Policy</button>
        <button onClick={() => navigate('/terms')}   style={{ background: 'none', border: 'none', color: '#8A8580', cursor: 'pointer', fontSize: 11 }}>Terms of Service</button>
      </div>
    </div>
  );
}
